export const COGO_DOCUMENT_TEMPLATE_SCHEMA_VERSION =
  "cogo.document-template.v1" as const;
export const COGO_DOCUMENT_GENERATION_IR_SCHEMA_VERSION =
  "cogo.document-generation-ir.v1" as const;
export const COGO_DOCUMENT_TEMPLATE_CANDIDATE_SCHEMA_VERSION =
  "cogo.document-template-authoring-candidate.v1" as const;

export const COGO_DOCUMENT_RENDER_TARGETS = [
  "docx",
  "hwpx",
  "pptx",
  "xlsx",
  "pdf",
] as const;

export type CogoDocumentRenderTarget =
  (typeof COGO_DOCUMENT_RENDER_TARGETS)[number];

export interface CogoDocumentTemplateColumn {
  key: string;
  label: string;
  widthRatio: number;
}

interface CogoDocumentTemplateSlotBase {
  id: string;
  required: boolean;
  bindingPath: string;
  heading: string;
}

export interface CogoDocumentTemplateRichTextSlot extends CogoDocumentTemplateSlotBase {
  kind: "rich-text";
}

export interface CogoDocumentTemplateTableSlot extends CogoDocumentTemplateSlotBase {
  kind: "table";
  columns: CogoDocumentTemplateColumn[];
}

export type CogoDocumentTemplateSlot =
  | CogoDocumentTemplateRichTextSlot
  | CogoDocumentTemplateTableSlot;

export interface CogoDocumentTemplateManifest {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_SCHEMA_VERSION;
  templateId: string;
  templateVersion: string;
  documentType: string;
  locale: string;
  staticContent: Record<string, string>;
  layout: {
    preset: "standard_business_brief";
    firstPagePattern: "memo_masthead";
    pageSize: "letter" | "a4";
    marginInches: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
    slotOrder: string[];
  };
  slots: CogoDocumentTemplateSlot[];
  assets: Array<{ role: string; path: string; sha256?: string }>;
  renderTargets: CogoDocumentRenderTarget[];
  publicationTargets?: Array<"google-docs" | "notion">;
}

export interface CogoDocumentTargetCapability {
  status:
    | "ready"
    | "adapter-required"
    | "source-template-required"
    | "static-only";
  adapter: string;
  actionFlowRole: string;
  unsupported: string[];
}

export interface CogoDocumentGenerationWarning {
  code: string;
  target: CogoDocumentRenderTarget;
  message: string;
  blocksFinalWrite: boolean;
}

export interface CogoDocumentGenerationIr {
  schemaVersion: typeof COGO_DOCUMENT_GENERATION_IR_SCHEMA_VERSION;
  generationId: string;
  createdAt: string;
  sourceRef: string;
  sourceHash: string;
  templateId: string;
  templateVersion: string;
  templateHash: string;
  semanticInputRef: string;
  semanticInputHash: string;
  compiler: {
    id: "cogo.document-template-ir-compiler";
    version: "1.0.0";
    deterministic: true;
    finalWriter: false;
  };
  staticContent: Record<string, string>;
  layout: CogoDocumentTemplateManifest["layout"];
  slots: CogoDocumentTemplateSlot[];
  bindings: Record<string, unknown>;
  requestedTargets: CogoDocumentRenderTarget[];
  targetCapabilities: Record<string, CogoDocumentTargetCapability>;
  warnings: CogoDocumentGenerationWarning[];
  structureHash: string;
  contentHash: string;
  evaluationContext?: {
    armId: string;
    method: string;
  };
}

export interface CogoDocumentTemplateAuthoringCandidate {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_CANDIDATE_SCHEMA_VERSION;
  candidateId: string;
  createdAt: string;
  status: "candidate";
  sourceRef: string;
  sourceHash: string;
  manifest: Record<string, unknown>;
  manifestHash: string;
  validationStatus: "pass" | "fail";
  validationErrors: string[];
  requiresHumanReview: true;
  modelCanonicalAuthority: false;
  modelFinalWriterAllowed: false;
}

export interface CogoDocumentTemplatePromotionReceipt {
  schemaVersion: "cogo.document-template-promotion-receipt.v1";
  promotionId: string;
  decidedAt: string;
  status: "promoted";
  candidateId: string;
  templateId: string;
  templateVersion: string;
  manifestHash: string;
  reviewerRef: string;
  reviewEvidenceRef: string;
  reviewEvidenceHash: string;
  humanApproved: true;
  automatedPromotion: false;
  modelCanonicalAuthority: false;
}

export const COGO_DOCUMENT_TARGET_CAPABILITIES: Record<
  CogoDocumentRenderTarget,
  CogoDocumentTargetCapability
> = {
  docx: {
    status: "ready",
    adapter: "cogo-document-renderer:docx",
    actionFlowRole:
      "Business events and approved document mutations; not WordprocessingML layout.",
    unsupported: [],
  },
  pdf: {
    status: "ready",
    adapter: "cogo-document-renderer:pdf",
    actionFlowRole:
      "Business events and publication orchestration; PDF remains a readback projection.",
    unsupported: ["editable-native-semantics"],
  },
  hwpx: {
    status: "ready",
    adapter: "cogo-document-renderer:hwpx",
    actionFlowRole:
      "Business events and approved mutations; HWPX package semantics stay in the target adapter.",
    unsupported: ["legacy-hwp-binary"],
  },
  pptx: {
    status: "static-only",
    adapter: "cogo-document-renderer:pptx-fixed-page",
    actionFlowRole:
      "Triggers and business state only; slide timing, transition, and motion belong to the PPTX capability adapter.",
    unsupported: [
      "presentationml-transition",
      "presentationml-timing",
      "click-triggered-shape-animation",
    ],
  },
  xlsx: {
    status: "adapter-required",
    adapter: "cogo-document-renderer:xlsx-source-free",
    actionFlowRole:
      "DocumentModel source-free table-text-and-merge-only is contract-defined until independent consumer and proof contracts are admitted. The SpreadsheetWorkbook source-template lane is separate and retains formula, style, chart, and package-part responsibilities.",
    unsupported: [
      "paragraph-placement",
      "page-number",
      "picture",
      "formula-authoring",
      "chart-authoring",
      "pivot-authoring",
      "macro-authoring",
      "external-workbook-reference-authoring",
    ],
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSha256(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function isSafeRelativePath(value: string): boolean {
  return (
    value.length > 0 &&
    !value.startsWith("/") &&
    !value.includes("\\") &&
    !value.split("/").includes("..")
  );
}

function unique(values: string[]): boolean {
  return new Set(values).size === values.length;
}

export function canonicalizeDocumentTemplateJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value
      .map((item) => canonicalizeDocumentTemplateJson(item))
      .join(",")}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${canonicalizeDocumentTemplateJson(value[key])}`,
      )
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export async function sha256CanonicalJson(value: unknown): Promise<string> {
  return sha256DocumentTemplateText(canonicalizeDocumentTemplateJson(value));
}

export async function sha256DocumentTemplateText(
  value: string,
): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function validateDocumentTemplateManifest(value: unknown): string[] {
  if (!isRecord(value)) return ["Template manifest must be an object."];
  const errors: string[] = [];
  if (value.schemaVersion !== COGO_DOCUMENT_TEMPLATE_SCHEMA_VERSION) {
    errors.push("Unsupported document template schemaVersion.");
  }
  if (
    typeof value.templateId !== "string" ||
    !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value.templateId)
  ) {
    errors.push("templateId must be a kebab-case identifier.");
  }
  if (
    typeof value.templateVersion !== "string" ||
    !/^\d+\.\d+\.\d+$/.test(value.templateVersion)
  ) {
    errors.push("templateVersion must be semantic version x.y.z.");
  }
  if (typeof value.documentType !== "string" || !value.documentType.trim()) {
    errors.push("documentType is required.");
  }
  if (typeof value.locale !== "string" || !value.locale.trim()) {
    errors.push("locale is required.");
  }
  if (
    !isRecord(value.staticContent) ||
    Object.keys(value.staticContent).length === 0 ||
    Object.values(value.staticContent).some(
      (entry) => typeof entry !== "string",
    )
  ) {
    errors.push("staticContent must contain string values.");
  }
  if (!isRecord(value.layout)) {
    errors.push("layout is required.");
  } else {
    if (value.layout.preset !== "standard_business_brief") {
      errors.push("Only standard_business_brief is admitted in template v1.");
    }
    if (value.layout.firstPagePattern !== "memo_masthead") {
      errors.push("Only memo_masthead is admitted in template v1.");
    }
    if (!["letter", "a4"].includes(String(value.layout.pageSize))) {
      errors.push("layout.pageSize must be letter or a4.");
    }
    if (!isRecord(value.layout.marginInches)) {
      errors.push("layout.marginInches is required.");
    } else {
      for (const side of ["top", "right", "bottom", "left"] as const) {
        const margin = value.layout.marginInches[side];
        if (typeof margin !== "number" || margin < 0 || margin > 3) {
          errors.push(`layout.marginInches.${side} must be between 0 and 3.`);
        }
      }
    }
  }
  if (!Array.isArray(value.slots) || value.slots.length === 0) {
    errors.push("slots must be a non-empty array.");
  } else {
    const slotIds: string[] = [];
    for (const [index, rawSlot] of value.slots.entries()) {
      if (!isRecord(rawSlot)) {
        errors.push(`slots[${index}] must be an object.`);
        continue;
      }
      if (typeof rawSlot.id !== "string" || !rawSlot.id.trim()) {
        errors.push(`slots[${index}].id is required.`);
      } else {
        slotIds.push(rawSlot.id);
      }
      if (!["rich-text", "table"].includes(String(rawSlot.kind))) {
        errors.push(`slots[${index}].kind is unsupported.`);
      }
      if (typeof rawSlot.required !== "boolean") {
        errors.push(`slots[${index}].required must be boolean.`);
      }
      if (
        typeof rawSlot.bindingPath !== "string" ||
        !rawSlot.bindingPath.startsWith("/")
      ) {
        errors.push(`slots[${index}].bindingPath must be a JSON pointer.`);
      }
      if (typeof rawSlot.heading !== "string" || !rawSlot.heading.trim()) {
        errors.push(`slots[${index}].heading is required.`);
      }
      if (rawSlot.kind === "table") {
        if (!Array.isArray(rawSlot.columns) || rawSlot.columns.length === 0) {
          errors.push(`slots[${index}].columns must be non-empty.`);
        } else {
          const keys: string[] = [];
          let width = 0;
          for (const [columnIndex, rawColumn] of rawSlot.columns.entries()) {
            if (!isRecord(rawColumn)) {
              errors.push(
                `slots[${index}].columns[${columnIndex}] must be an object.`,
              );
              continue;
            }
            if (typeof rawColumn.key !== "string" || !rawColumn.key.trim()) {
              errors.push(
                `slots[${index}].columns[${columnIndex}].key is required.`,
              );
            } else {
              keys.push(rawColumn.key);
            }
            if (
              typeof rawColumn.label !== "string" ||
              !rawColumn.label.trim()
            ) {
              errors.push(
                `slots[${index}].columns[${columnIndex}].label is required.`,
              );
            }
            if (
              typeof rawColumn.widthRatio !== "number" ||
              rawColumn.widthRatio <= 0 ||
              rawColumn.widthRatio > 1
            ) {
              errors.push(
                `slots[${index}].columns[${columnIndex}].widthRatio is invalid.`,
              );
            } else {
              width += rawColumn.widthRatio;
            }
          }
          if (!unique(keys))
            errors.push(`slots[${index}] column keys must be unique.`);
          if (Math.abs(width - 1) > 0.0001) {
            errors.push(
              `slots[${index}] column widthRatio values must sum to 1.`,
            );
          }
        }
      }
    }
    if (!unique(slotIds)) errors.push("Template slot ids must be unique.");
    const slotOrder = isRecord(value.layout) ? value.layout.slotOrder : null;
    if (
      !Array.isArray(slotOrder) ||
      JSON.stringify(slotOrder) !== JSON.stringify(slotIds)
    ) {
      errors.push("layout.slotOrder must exactly match declared slot order.");
    }
  }
  if (
    !Array.isArray(value.renderTargets) ||
    value.renderTargets.length === 0 ||
    !unique(
      value.renderTargets.filter(
        (target): target is string => typeof target === "string",
      ),
    ) ||
    value.renderTargets.some(
      (target) =>
        !COGO_DOCUMENT_RENDER_TARGETS.includes(
          target as CogoDocumentRenderTarget,
        ),
    )
  ) {
    errors.push(
      "renderTargets must be a unique non-empty supported target list.",
    );
  }
  if (!Array.isArray(value.assets)) {
    errors.push("assets must be an array.");
  } else {
    for (const [index, rawAsset] of value.assets.entries()) {
      if (
        !isRecord(rawAsset) ||
        typeof rawAsset.role !== "string" ||
        typeof rawAsset.path !== "string" ||
        !isSafeRelativePath(rawAsset.path) ||
        (rawAsset.sha256 !== undefined && !isSha256(rawAsset.sha256))
      ) {
        errors.push(`assets[${index}] is invalid or unsafe.`);
      }
    }
  }
  return [...new Set(errors)];
}

function validateJsonSchemaSubset(
  schema: unknown,
  value: unknown,
  pointer = "$",
  rootSchema: unknown = schema,
): string[] {
  if (!isRecord(schema)) return [`${pointer}: schema must be an object.`];
  if (typeof schema.$ref === "string") {
    if (!schema.$ref.startsWith("#/")) {
      return [`${pointer}: only local JSON Schema references are supported.`];
    }
    let resolved: unknown = rootSchema;
    for (const rawPart of schema.$ref.slice(2).split("/")) {
      const part = rawPart.replaceAll("~1", "/").replaceAll("~0", "~");
      if (!isRecord(resolved) || !(part in resolved)) {
        return [`${pointer}: JSON Schema reference does not resolve.`];
      }
      resolved = resolved[part];
    }
    return validateJsonSchemaSubset(resolved, value, pointer, rootSchema);
  }
  const errors: string[] = [];
  if (schema.const !== undefined && value !== schema.const) {
    errors.push(`${pointer}: value does not match const.`);
  }
  if (Array.isArray(schema.enum) && !schema.enum.includes(value)) {
    errors.push(`${pointer}: value is not in enum.`);
  }
  if (schema.type === "object") {
    if (!isRecord(value)) return [`${pointer}: expected object.`];
    if (
      typeof schema.minProperties === "number" &&
      Object.keys(value).length < schema.minProperties
    ) {
      errors.push(
        `${pointer}: requires at least ${schema.minProperties} properties.`,
      );
    }
    const required = Array.isArray(schema.required)
      ? schema.required.filter((key): key is string => typeof key === "string")
      : [];
    for (const key of required) {
      if (!(key in value))
        errors.push(`${pointer}/${key}: required property missing.`);
    }
    const properties = isRecord(schema.properties) ? schema.properties : {};
    for (const [key, child] of Object.entries(value)) {
      if (key in properties) {
        errors.push(
          ...validateJsonSchemaSubset(
            properties[key],
            child,
            `${pointer}/${key}`,
            rootSchema,
          ),
        );
      } else if (schema.additionalProperties === false) {
        errors.push(`${pointer}/${key}: additional property is not allowed.`);
      } else if (isRecord(schema.additionalProperties)) {
        errors.push(
          ...validateJsonSchemaSubset(
            schema.additionalProperties,
            child,
            `${pointer}/${key}`,
            rootSchema,
          ),
        );
      }
    }
  } else if (schema.type === "array") {
    if (!Array.isArray(value)) return [`${pointer}: expected array.`];
    if (typeof schema.minItems === "number" && value.length < schema.minItems) {
      errors.push(`${pointer}: requires at least ${schema.minItems} items.`);
    }
    if (
      schema.uniqueItems === true &&
      new Set(value.map((item) => canonicalizeDocumentTemplateJson(item)))
        .size !== value.length
    ) {
      errors.push(`${pointer}: array items must be unique.`);
    }
    if (schema.items) {
      value.forEach((item, index) =>
        errors.push(
          ...validateJsonSchemaSubset(
            schema.items,
            item,
            `${pointer}/${index}`,
            rootSchema,
          ),
        ),
      );
    }
  } else if (schema.type === "string") {
    if (typeof value !== "string") return [`${pointer}: expected string.`];
    if (
      typeof schema.minLength === "number" &&
      value.length < schema.minLength
    ) {
      errors.push(`${pointer}: string is shorter than ${schema.minLength}.`);
    }
    if (
      typeof schema.pattern === "string" &&
      !new RegExp(schema.pattern).test(value)
    ) {
      errors.push(`${pointer}: string does not match pattern.`);
    }
    if (
      schema.format === "date-time" &&
      (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
        value,
      ) ||
        Number.isNaN(Date.parse(value)))
    ) {
      errors.push(`${pointer}: string is not a valid date-time.`);
    }
  } else if (schema.type === "number" && typeof value !== "number") {
    errors.push(`${pointer}: expected number.`);
  } else if (schema.type === "integer" && !Number.isInteger(value)) {
    errors.push(`${pointer}: expected integer.`);
  } else if (schema.type === "boolean" && typeof value !== "boolean") {
    errors.push(`${pointer}: expected boolean.`);
  }
  return errors;
}

export function validateDocumentTemplateJsonSchema(
  schema: unknown,
  value: unknown,
): string[] {
  return validateJsonSchemaSubset(schema, value, "$", schema);
}

export function validateDocumentTemplateInput(
  inputSchema: unknown,
  semanticInput: unknown,
): string[] {
  return validateDocumentTemplateJsonSchema(inputSchema, semanticInput);
}

export function resolveDocumentTemplatePointer(
  value: unknown,
  pointer: string,
): unknown {
  if (!pointer.startsWith("/")) {
    throw new Error(`Binding path must be a JSON pointer: ${pointer}`);
  }
  let current = value;
  for (const rawPart of pointer.slice(1).split("/")) {
    const part = rawPart.replaceAll("~1", "/").replaceAll("~0", "~");
    if (!isRecord(current) || !(part in current)) {
      throw new Error(`Binding path does not resolve: ${pointer}`);
    }
    current = current[part];
  }
  return current;
}

export function bindDocumentTemplateSlots(input: {
  template: CogoDocumentTemplateManifest;
  semanticInput: unknown;
}): Record<string, unknown> {
  const bindings: Record<string, unknown> = {};
  for (const slot of input.template.slots) {
    let resolved: unknown;
    try {
      resolved = resolveDocumentTemplatePointer(
        input.semanticInput,
        slot.bindingPath,
      );
    } catch (error) {
      if (!slot.required) continue;
      throw error;
    }
    if (slot.kind === "rich-text") {
      if (typeof resolved !== "string" || !resolved.trim()) {
        throw new Error(`Slot ${slot.id} requires non-empty rich text.`);
      }
    } else {
      if (!Array.isArray(resolved) || resolved.length === 0) {
        throw new Error(`Slot ${slot.id} requires at least one table row.`);
      }
      for (const [rowIndex, row] of resolved.entries()) {
        if (!isRecord(row)) {
          throw new Error(`Slot ${slot.id} row ${rowIndex} must be an object.`);
        }
        for (const column of slot.columns) {
          const cell = row[column.key];
          if (typeof cell !== "string" || !cell.trim()) {
            throw new Error(
              `Slot ${slot.id} row ${rowIndex} requires ${column.key}.`,
            );
          }
          if (
            /evidenceRef$/i.test(column.key) &&
            !cell.startsWith("source://")
          ) {
            throw new Error(
              `Slot ${slot.id} row ${rowIndex} evidence must use source:// lineage.`,
            );
          }
        }
      }
    }
    bindings[slot.id] = structuredClone(resolved);
  }
  return bindings;
}

function warningsForTarget(
  target: CogoDocumentRenderTarget,
): CogoDocumentGenerationWarning[] {
  const capability = COGO_DOCUMENT_TARGET_CAPABILITIES[target];
  if (capability.status === "ready") return [];
  return [
    {
      code: `document-target-${capability.status}`,
      target,
      message:
        target === "pptx"
          ? "PPTX generation is static-only until PresentationML transition/timing readback is implemented."
          : target === "xlsx"
            ? "XLSX source-free table-text-and-merge-only is contract-defined; final writing remains blocked until independent consumer and proof contracts are admitted."
            : `${target.toUpperCase()} final writing requires its target adapter.`,
      blocksFinalWrite: target === "xlsx",
    },
  ];
}

export async function compileDocumentTemplate(input: {
  template: unknown;
  inputSchema: unknown;
  semanticInput: unknown;
  sourceRef: string;
  sourceHash: string;
  semanticInputRef: string;
  semanticInputHash?: string;
  templateHash?: string;
  requestedTargets?: CogoDocumentRenderTarget[];
  createdAt: string;
}): Promise<CogoDocumentGenerationIr> {
  const templateErrors = validateDocumentTemplateManifest(input.template);
  if (templateErrors.length > 0) {
    throw new Error(`Invalid document template: ${templateErrors.join(" ")}`);
  }
  if (!isSha256(input.sourceHash))
    throw new Error("sourceHash must be SHA-256.");
  if (!input.sourceRef.trim()) throw new Error("sourceRef is required.");
  if (!input.semanticInputRef.trim()) {
    throw new Error("semanticInputRef is required.");
  }
  if (Number.isNaN(Date.parse(input.createdAt))) {
    throw new Error("createdAt must be an ISO date-time.");
  }
  const template = structuredClone(
    input.template,
  ) as CogoDocumentTemplateManifest;
  const inputErrors = validateDocumentTemplateInput(
    input.inputSchema,
    input.semanticInput,
  );
  if (inputErrors.length > 0) {
    throw new Error(`Invalid semantic input: ${inputErrors.join(" ")}`);
  }
  const requestedTargets = input.requestedTargets ?? template.renderTargets;
  if (requestedTargets.length === 0 || !unique(requestedTargets)) {
    throw new Error("requestedTargets must be unique and non-empty.");
  }
  for (const target of requestedTargets) {
    if (!template.renderTargets.includes(target)) {
      throw new Error(
        `Template ${template.templateId} does not declare target ${target}.`,
      );
    }
  }
  const bindings = bindDocumentTemplateSlots({
    template,
    semanticInput: input.semanticInput,
  });
  const templateHash =
    input.templateHash ?? (await sha256CanonicalJson(template));
  const semanticInputHash =
    input.semanticInputHash ?? (await sha256CanonicalJson(input.semanticInput));
  if (!isSha256(templateHash) || !isSha256(semanticInputHash)) {
    throw new Error("templateHash and semanticInputHash must be SHA-256.");
  }
  const structureBasis = {
    templateId: template.templateId,
    templateVersion: template.templateVersion,
    staticContent: template.staticContent,
    layout: template.layout,
    slots: template.slots.map(({ required: _required, ...slot }) => slot),
  };
  const contentBasis = {
    sourceHash: input.sourceHash,
    semanticInputHash,
    bindings,
  };
  const structureHash = await sha256CanonicalJson(structureBasis);
  const contentHash = await sha256CanonicalJson(contentBasis);
  const generationId = `${template.templateId}.${template.templateVersion}.${contentHash.slice(-16)}`;
  const targetCapabilities = Object.fromEntries(
    requestedTargets.map((target) => [
      target,
      structuredClone(COGO_DOCUMENT_TARGET_CAPABILITIES[target]),
    ]),
  );
  return {
    schemaVersion: COGO_DOCUMENT_GENERATION_IR_SCHEMA_VERSION,
    generationId,
    createdAt: input.createdAt,
    sourceRef: input.sourceRef,
    sourceHash: input.sourceHash,
    templateId: template.templateId,
    templateVersion: template.templateVersion,
    templateHash,
    semanticInputRef: input.semanticInputRef,
    semanticInputHash,
    compiler: {
      id: "cogo.document-template-ir-compiler",
      version: "1.0.0",
      deterministic: true,
      finalWriter: false,
    },
    staticContent: template.staticContent,
    layout: template.layout,
    slots: template.slots,
    bindings,
    requestedTargets,
    targetCapabilities,
    warnings: requestedTargets.flatMap(warningsForTarget),
    structureHash,
    contentHash,
  };
}

export async function createDocumentTemplateAuthoringCandidate(input: {
  candidateId: string;
  createdAt: string;
  sourceRef: string;
  sourceHash: string;
  manifest: unknown;
}): Promise<CogoDocumentTemplateAuthoringCandidate> {
  if (!input.candidateId.trim()) throw new Error("candidateId is required.");
  if (!input.sourceRef.trim()) throw new Error("sourceRef is required.");
  if (!isSha256(input.sourceHash))
    throw new Error("sourceHash must be SHA-256.");
  if (Number.isNaN(Date.parse(input.createdAt))) {
    throw new Error("createdAt must be an ISO date-time.");
  }
  const validationErrors = validateDocumentTemplateManifest(input.manifest);
  if (!isRecord(input.manifest)) {
    throw new Error("Template authoring candidate manifest must be an object.");
  }
  const manifest = structuredClone(input.manifest);
  return {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_CANDIDATE_SCHEMA_VERSION,
    candidateId: input.candidateId,
    createdAt: input.createdAt,
    status: "candidate",
    sourceRef: input.sourceRef,
    sourceHash: input.sourceHash,
    manifest,
    manifestHash: await sha256CanonicalJson(manifest),
    validationStatus: validationErrors.length === 0 ? "pass" : "fail",
    validationErrors,
    requiresHumanReview: true,
    modelCanonicalAuthority: false,
    modelFinalWriterAllowed: false,
  };
}

export async function promoteDocumentTemplateCandidate(input: {
  candidate: CogoDocumentTemplateAuthoringCandidate;
  reviewerRef: string;
  reviewEvidenceRef: string;
  reviewEvidenceHash: string;
  humanApproved: boolean;
  decidedAt: string;
}): Promise<CogoDocumentTemplatePromotionReceipt> {
  const validationErrors = validateDocumentTemplateManifest(
    input.candidate.manifest,
  );
  if (
    input.candidate.validationStatus !== "pass" ||
    validationErrors.length > 0
  ) {
    throw new Error(
      "A template candidate cannot be promoted before validation passes.",
    );
  }
  const observedManifestHash = await sha256CanonicalJson(
    input.candidate.manifest,
  );
  if (observedManifestHash !== input.candidate.manifestHash) {
    throw new Error(
      "Template candidate manifestHash does not match its manifest.",
    );
  }
  if (!input.humanApproved) {
    throw new Error("Template promotion requires explicit human approval.");
  }
  if (!input.reviewerRef.trim() || !input.reviewEvidenceRef.trim()) {
    throw new Error(
      "Template promotion requires reviewer and review evidence refs.",
    );
  }
  if (!isSha256(input.reviewEvidenceHash)) {
    throw new Error("reviewEvidenceHash must be SHA-256.");
  }
  if (Number.isNaN(Date.parse(input.decidedAt))) {
    throw new Error("decidedAt must be an ISO date-time.");
  }
  const manifest = input.candidate
    .manifest as unknown as CogoDocumentTemplateManifest;
  return {
    schemaVersion: "cogo.document-template-promotion-receipt.v1",
    promotionId: `${input.candidate.candidateId}.promotion`,
    decidedAt: input.decidedAt,
    status: "promoted",
    candidateId: input.candidate.candidateId,
    templateId: manifest.templateId,
    templateVersion: manifest.templateVersion,
    manifestHash: input.candidate.manifestHash,
    reviewerRef: input.reviewerRef,
    reviewEvidenceRef: input.reviewEvidenceRef,
    reviewEvidenceHash: input.reviewEvidenceHash,
    humanApproved: true,
    automatedPromotion: false,
    modelCanonicalAuthority: false,
  };
}
